<template>
    <Head title="Versions application" />

    <AppLayout>
        <div class="max-w-5xl mx-auto space-y-6">
            <div class="flex items-center justify-between">
                <div>
                    <h1 class="text-xl font-bold text-gray-800">Versions application</h1>
                    <p class="text-sm text-gray-500 mt-1">
                        Gérez les versions Android et iOS proposées aux utilisateurs mobiles.
                    </p>
                </div>
            </div>

            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div v-for="platform in platforms" :key="platform"
                    class="bg-white shadow-lg rounded-sm border border-gray-200 p-5"
                >
                    <div class="flex items-center justify-between mb-4">
                        <h2 class="text-lg font-semibold text-sky capitalize">{{ platform }}</h2>
                        <span class="text-xs font-semibold px-2 py-1 rounded"
                            :class="forms[platform].force_update ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'"
                        >
                            {{ forms[platform].force_update ? 'Mise à jour forcée' : 'Mise à jour optionnelle' }}
                        </span>
                    </div>

                    <div class="space-y-3">
                        <div class="grid grid-cols-2 gap-3">
                            <div>
                                <JetLabelRequired :for="platform + '-latest'" value="Dernière version" />
                                <JetInput
                                    :id="platform + '-latest'"
                                    type="text"
                                    v-model="forms[platform].latest_version"
                                    placeholder="1.2.2"
                                    maxlength="10"
                                />
                                <JetInputError :message="forms[platform].errors.latest_version" />
                            </div>
                            <div>
                                <JetLabelRequired :for="platform + '-minimum'" value="Version minimale" />
                                <JetInput
                                    :id="platform + '-minimum'"
                                    type="text"
                                    v-model="forms[platform].minimum_version"
                                    placeholder="1.2.1"
                                    maxlength="10"
                                />
                                <JetInputError :message="forms[platform].errors.minimum_version" />
                            </div>
                        </div>

                        <div>
                            <JetLabelRequired :for="platform + '-title'" value="Titre" />
                            <JetInput
                                :id="platform + '-title'"
                                type="text"
                                v-model="forms[platform].title"
                                maxlength="255"
                            />
                            <JetInputError :message="forms[platform].errors.title" />
                        </div>

                        <div>
                            <JetLabelRequired :for="platform + '-message'" value="Message" />
                            <textarea
                                :id="platform + '-message'"
                                v-model="forms[platform].message"
                                rows="8"
                                class="border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 rounded-md shadow-sm mt-1 block w-full text-sm"
                            />
                            <JetInputError :message="forms[platform].errors.message" />
                        </div>

                        <div v-if="platform === 'android'">
                            <JetLabel :for="platform + '-play'" value="URL Play Store" />
                            <JetInput
                                :id="platform + '-play'"
                                type="url"
                                v-model="forms[platform].play_store_url"
                            />
                            <JetInputError :message="forms[platform].errors.play_store_url" />
                        </div>

                        <div v-else>
                            <JetLabel :for="platform + '-app'" value="URL App Store" />
                            <JetInput
                                :id="platform + '-app'"
                                type="url"
                                v-model="forms[platform].app_store_url"
                            />
                            <JetInputError :message="forms[platform].errors.app_store_url" />
                        </div>

                        <label class="flex items-center space-x-2 cursor-pointer">
                            <input
                                type="checkbox"
                                class="rounded border-gray-300 text-sky shadow-sm focus:border-sky focus:ring focus:ring-sky focus:ring-opacity-50"
                                v-model="forms[platform].force_update"
                            >
                            <span class="text-sm text-gray-700">Forcer la mise à jour</span>
                        </label>

                        <button
                            class="btn w-full bg-sky hover:bg-sky-dark text-white uppercase mt-2"
                            :disabled="forms[platform].processing"
                            :class="{ 'opacity-25': forms[platform].processing }"
                            @click.prevent="save(platform)"
                        >
                            Enregistrer {{ platform }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </AppLayout>
</template>

<script>
    import AppLayout from '@/Layouts/AppLayout.vue'
    import JetInput from '@/Jetstream/Input.vue'
    import JetLabel from '@/Jetstream/Label.vue'
    import JetLabelRequired from '@/Jetstream/LabelRequired.vue'
    import JetInputError from '@/Jetstream/InputError.vue'
    import { Head } from '@inertiajs/inertia-vue3'

    export default {
        components: {
            Head,
            AppLayout,
            JetInput,
            JetLabel,
            JetLabelRequired,
            JetInputError,
        },

        props: {
            versions: {
                type: Object,
                required: true,
            },
        },

        data() {
            const forms = {}

            ;['android', 'ios'].forEach((platform) => {
                const version = this.versions[platform] || {}
                forms[platform] = this.$inertia.form({
                    platform,
                    latest_version: version.latest_version || '',
                    minimum_version: version.minimum_version || '',
                    force_update: !!version.force_update,
                    title: version.title || '',
                    message: version.message || '',
                    play_store_url: version.play_store_url || '',
                    app_store_url: version.app_store_url || '',
                })
            })

            return {
                platforms: ['android', 'ios'],
                forms,
            }
        },

        methods: {
            save(platform) {
                this.forms[platform].put(route('app-versions.update'), {
                    preserveScroll: true,
                    onSuccess: () => {
                        this.$swal.fire(
                            'Versions',
                            `Version ${platform} enregistrée avec succès.`,
                            'success'
                        )
                    },
                })
            },
        },
    }
</script>
